import { X } from 'lucide-react'
import React from 'react'

import { cn } from '@/utilities/ui'

/** Temporary kill switch for hover-card body copy. */
export const MAP_HOVER_CARD_DESCRIPTION_ENABLED = false

export const mapHoverCardMaxWidthClassName =
  'w-[min(20rem,calc(100vw-3rem))] max-w-[20rem] md:w-[min(26.25rem,calc(100vw-2rem))] md:max-w-[26.25rem]'

export const mapHoverCardPrimaryLinkClassName =
  'inline-flex h-12 items-center justify-center rounded-[0.625rem] border-[1.5px] border-black/10 bg-turquoise px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors hover:bg-turquoise/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 max-md:h-10 max-md:text-sm'

export const mapHoverCardSecondaryLinkClassName =
  'inline-flex h-12 min-w-0 flex-1 items-center justify-center rounded-[0.625rem] border-[1.5px] border-black/30 bg-white/30 px-3 text-center font-outfit text-base font-medium leading-tight text-dark-800/80 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 max-md:h-10 max-md:px-2 max-md:text-sm'

type MapHoverCardShellProps = {
  actions: React.ReactNode
  className?: string
  closeLabel?: string
  description?: string
  id?: string
  name: string
  onClose?: () => void
  previewImage?: string
  previewImageAlt?: string
}

export function MapHoverCardShell({
  actions,
  className,
  closeLabel = 'Close',
  description,
  id,
  name,
  onClose,
  previewImage,
  previewImageAlt,
}: MapHoverCardShellProps) {
  return (
    <article
      className={cn(
        'relative w-full overflow-hidden rounded-[1.375rem] border-[1.5px] border-black/10 bg-white p-3 shadow-[0px_16px_32px_-8px_rgba(0,33,64,0.12)] max-md:rounded-[1rem] max-md:p-2',
        mapHoverCardMaxWidthClassName,
        className,
      )}
      id={id}
    >
      {onClose ? (
        <button
          aria-label={closeLabel}
          className="absolute top-5 right-5 z-10 inline-flex size-10 items-center justify-center rounded-full border-[1.5px] border-black/10 bg-white/90 text-dark-800/80 shadow-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 max-md:top-3 max-md:right-3 max-md:size-9"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden className="size-5" />
        </button>
      ) : null}

      {previewImage ? (
        <img
          alt={previewImageAlt ?? ''}
          className="h-[16.5rem] w-full max-w-full rounded-[0.625rem] object-cover max-md:h-[10.5rem]"
          height={264}
          src={previewImage}
          width={396}
        />
      ) : null}

      <div className="flex flex-col gap-0 pt-3 max-md:gap-3 max-md:px-1 max-md:py-2">
        <h3 className="min-h-[2.5rem] font-display text-[1.75rem] font-medium leading-8 tracking-[-0.04em] text-dark-800/80 max-md:min-h-[2rem] max-md:text-[1.375rem] max-md:leading-7">
          {name}
        </h3>
        {MAP_HOVER_CARD_DESCRIPTION_ENABLED && description ? (
          <p className="line-clamp-4 font-outfit text-base leading-5 text-dark-800/80 max-md:text-sm max-md:leading-4">
            {description}
          </p>
        ) : null}
        {actions}
      </div>
    </article>
  )
}
